import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { Config } from "../config";
import { LicenseValidationRequest } from "../interfaces/license/license-validation-request.interface";
import { LicenseValidationResponse } from "../interfaces/license/license-validation-response.interface";
import { LicenseSession } from "../models/license/license-session.model";
import { TraceIdService } from "./tools/trace-id.service";
import { StorageService } from "./storage.service";
import { LoggingService } from "./logging.service";

/**
 * Service for validating licenses and managing license sessions
 */
@Injectable({
    providedIn: "root",
})
export class LicenseValidationService {
    private get api(): string {
        return Config.environment.apiDynamic
            ? `${window.location.protocol}//${window.location.host}`
            : Config.environment.api;
    }

    constructor(
        private http: HttpClient,
        private traceIdService: TraceIdService,
        private storageSrv: StorageService,
        private loggingService: LoggingService,
    ) {
    }

    /**
     * Validate a license with the API
     * Automatically generates and propagates W3C TraceID
     *
     * @param license - License key to validate
     * @param origin - Origin of the request (defaults to environment config)
     * @param metadata - Optional metadata to include in the request
     * @returns Observable<LicenseSession>
     */
    public validateLicense(
        license: string,
        origin?: string,
        metadata?: any,
    ): Observable<LicenseSession> {
        // Use origin from environment config if not provided
        const requestOrigin = origin || Config.environment.license.defaultMetadata.origin;

        // Generate W3C TraceID if enabled in config
        let headers = new HttpHeaders({
            "Content-Type": "application/json",
        });

        let traceId: string | undefined;

        if (Config.environment.license.tracing.enabled) {
            const traceContext = this.traceIdService.generateTraceContext(
                Config.environment.license.tracing.sampled
            );

            // Only add traceparent header if we have a valid trace context (with session trace ID)
            if (traceContext) {
                // Add traceparent header
                headers = headers.set("traceparent", traceContext.traceparent);

                // Extract trace_id from traceparent (format: 00-{traceId}-{spanId}-{flags})
                const traceparentParts = traceContext.traceparent.split('-');
                if (traceparentParts.length === 4) {
                    traceId = traceparentParts[1]; // The trace ID is the second part
                }

                // Log trace context for debugging
                this.loggingService.log(
                    `License validation - TraceID: ${traceContext.traceparent}`,
                    false,
                    {
                        license,
                        origin: requestOrigin,
                        traceparent: traceContext.traceparent,
                        extractedTraceId: traceId,
                    },
                );
            }
        }

        const requestBody: LicenseValidationRequest = {
            license: 'L' + (traceId || license), // Use trace_id as license value, fallback to license param
            app_id: Config.environment.license.defaultMetadata.app_id,
            origin: Config.environment.license.defaultMetadata.origin, // web, ios, android
            product: Config.environment.license.defaultMetadata.product,
            metadata: {
                sdk_version: Config.environment.license.defaultMetadata.sdk_version
            },
        };

        // Make API request using configured endpoint
        const endpoint = `${this.api}${Config.environment.license.endpoint}`;

        return this.http
            .post<LicenseValidationResponse>(
                endpoint,
                requestBody,
                {headers},
            )
            .pipe(
                map((response: LicenseValidationResponse) => {
                    // Create LicenseSession model
                    const licenseSession = new LicenseSession(response);

                    // Store session
                    this.storeLicenseSession(licenseSession);

                    // Log successful validation
                    this.loggingService.log(
                        "License validated successfully",
                        false,
                        {
                            session_id: licenseSession.sessionId,
                            jti: licenseSession.jti,
                            step: licenseSession.step,
                            expires_at: licenseSession.expiresAt.format(),
                        },
                    );

                    return licenseSession;
                }),
            );
    }

    /**
     * Store license session in encrypted storage
     * @param licenseSession - LicenseSession to store
     */
    private storeLicenseSession(licenseSession: LicenseSession): void {
        this.storageSrv.setLocalStorageEncrypted(
            "_cap_license_session",
            licenseSession,
        );
    }

    /**
     * Get stored license session from storage
     * @returns LicenseSession or null if not found or expired
     */
    public getLicenseSession(): LicenseSession | null {
        const storedSession = this.storageSrv.getLocalStorageDecrypted<any>(
            "_cap_license_session",
        );

        if (!storedSession) {
            return null;
        }

        const licenseSession = new LicenseSession(storedSession);

        // Check if expired
        if (licenseSession.hasExpired()) {
            this.clearLicenseSession();
            return null;
        }

        return licenseSession;
    }

    /**
     * Clear license session from storage
     */
    public clearLicenseSession(): void {
        this.storageSrv.removeLocalStorage("_cap_license_session");
    }

    /**
     * Check if a valid license session exists
     * @returns true if a valid license exists, false otherwise
     */
    public hasValidLicenseSession(): boolean {
        return this.getLicenseSession() !== null;
    }
}
